import { useState, useEffect, useCallback, useRef, useLayoutEffect, createContext, useContext } from 'react';
import ReactDOM from 'react-dom';
import {
    ArrowLeft, Save, Loader2, Plus, Trash2, ChevronUp, ChevronDown,
    FileText, Video, FileDown, GripVertical, Check, X, Upload, Eye, ChevronLeft, ChevronRight,
    BookOpen, ClipboardCheck, Award, BarChart3, Users, Settings, Play, Archive,
    Link as LinkIcon, RefreshCw, AlertTriangle
} from 'lucide-react';
import TipTapEditor from '../../TipTapEditor';
import { PdfViewer } from '../../../pages/MojeSkoleniDetail';
import Dropdown from '../../receptura/Dropdown';
import usePotvrzeni from '../../ui/usePotvrzeni';
import ZpetTlacitko from '../../ui/ZpetTlacitko';

const SECTION_COLOR = '#B47AEA';
const TYP_LABELS = { skoleni: 'Pouze školením', kviz: 'Kvízem', kviz_certifikace: 'Kvíz + Certifikace', kviz_kvalifikace: 'Kvíz + Kval. matice' };
const SEKCE_TYP_ICONS = { obsah: FileText, video: Video, soubor: FileDown };
const TABS = [
    { id: 'obsah', label: 'Obsah', Icon: FileText },
    { id: 'nahled', label: 'Náhled', Icon: Eye },
    { id: 'kviz', label: 'Kvíz', Icon: ClipboardCheck },
    { id: 'nastaveni', label: 'Nastavení', Icon: Settings },
    { id: 'prirazeni', label: 'Přiřazení', Icon: Users },
    { id: 'vysledky', label: 'Výsledky', Icon: BarChart3 },
];

const token = () => document.querySelector('meta[name="csrf-token"]')?.content;
const api = async (url, opts = {}) => {
    const resp = await fetch(url, { headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token(), ...opts.headers }, ...opts });
    return resp.json();
};

const formatDate = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    if (isNaN(d)) return '—';
    return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`;
};
const formatDateTime = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    if (isNaN(d)) return '—';
    return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
};

const inputCls = "w-full px-3 py-2 rounded-lg text-sm";
const inputStyle = { background: 'var(--surface-elevated)', border: '1px solid var(--border-default)', color: 'var(--text-primary)', outline: 'none', colorScheme: 'inherit' };
const optionStyle = {}; // uses colorScheme inherit from parent
const labelCls = "text-xs font-medium mb-1 block";
const labelStyle = { color: 'var(--text-muted)' };

// Podzáložky editoru se ptají na potvrzení a motiv přes kontext — ať se
// `usePotvrzeni` vykresluje jednou, v detailu, a ne v každé záložce zvlášť.
const PotvrditCtx = createContext(async () => false);
const TmavyCtx = createContext(false);
// Na mobilu je editor zobrazení a kontrola (norma §1b): bez přetahování (HTML5
// drag & drop na dotyku nefunguje) a bez mazání.
const MobilCtx = createContext(false);

/** Výběr hodnoty v editoru — naše `Dropdown`, nikdy nativní `<select>`. */
function Vyber({ value, onChange, options, placeholder = 'Vyberte…' }) {
    const isDark = useContext(TmavyCtx);
    return (
        <Dropdown value={value ?? ''} onChange={onChange} options={options} placeholder={placeholder}
            isDark={isDark} accent={SECTION_COLOR} />
    );
}

const STAV_LABEL = { koncept: 'Koncept', aktivni: 'Aktivní', archivovano: 'Archivováno' };

// ═══════════════════════════════════════════════════════════
/**
 * Detail školení v Lidé → Školení (druhý sloupec `ModulLayout`). Dřív
 * samostatná stránka, která seznam zavřela úplně — zpátky vedla jen šipka.
 *
 * @param {number} props.id
 * @param {Function} props.onZavrit - zavře detail (mobil „Zpět", po smazání)
 * @param {Function} [props.onZmena] - uložená změna, ať se obnoví seznam
 * @param {(edituje: boolean) => void} [props.onEditace] - edituje se obsah
 *        sekce; modul pak schová třetí sloupec, ať má editor šířku
 * @param {React.ReactNode} [props.nadZalozkami] - na mobilu řádek „K dořešení" (třetí sloupec tam není)
 */
export default function SkoleniEditor({ id, isDark = false, isMobile = false, onZavrit, onZmena, onEditace, rezervaDole = null, nadZalozkami = null }) {
    const [potvrdit, potvrzeni] = usePotvrzeni(isDark);
    // Záložky editoru volají `onNavigate` po smazání školení — tady to znamená zavřít detail.
    const onNavigate = () => { onZmena?.(); onZavrit?.(); };
    const [skoleni, setSkoleni] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('obsah');

    // Data for assignment tab
    const [ciselniky, setCiselniky] = useState({ zamestnanci: [], oddeleni: [], pozice: [], kariery: [] });
    const [vysledky, setVysledky] = useState([]);
    const [vysledkyOtazky, setVysledkyOtazky] = useState([]);

    const fetchSkoleni = useCallback(() => {
        fetch(`/api/skoleni/${id}`).then(r => r.json()).then(data => {
            setSkoleni(data);
            setLoading(false);
        });
    }, [id]);
    const obnovit = () => { fetchSkoleni(); onZmena?.(); };

    // Jiné školení = od začátku, na první záložce
    useEffect(() => { setLoading(true); setSkoleni(null); setTab('obsah'); onEditace?.(false); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetchSkoleni();
        // Load číselníky for assignment
        Promise.all([
            fetch('/api/zamestnanci').then(r => r.json()),
            fetch('/api/ciselniky/oddeleni').then(r => r.json()),
            fetch('/api/ciselniky/pozice').then(r => r.json()),
            fetch('/api/ciselniky/kariery').then(r => r.json()),
        ]).then(([zam, odd, poz, kar]) => {
            setCiselniky({ zamestnanci: zam, oddeleni: odd, pozice: poz, kariery: kar });
        });
    }, [fetchSkoleni]);

    const saveSkoleni = async (updates) => {
        const data = await api(`/api/skoleni/${id}`, { method: 'PUT', body: JSON.stringify(updates) });
        setSkoleni(prev => ({ ...prev, ...data }));
        onZmena?.();
        return data;
    };

    if (loading || !skoleni) {
        return <div className="flex items-center justify-center" style={{ minHeight: 300, color: 'var(--text-muted)' }}><Loader2 size={24} className="animate-spin" /></div>;
    }

    const hasKviz = skoleni.typ_zakonceni !== 'skoleni';

    const zalozky = TABS.filter(t => t.id !== 'kviz' || hasKviz);
    const prepni = (tid) => {
        setTab(tid);
        if (tid !== 'obsah') onEditace?.(false);
        if (tid === 'vysledky') {
            fetch(`/api/skoleni/${id}/vysledky`).then(r => r.json()).then(data => {
                // Backend vrací { otazky, prubehy }
                if (Array.isArray(data)) { setVysledky(data); setVysledkyOtazky([]); }
                else { setVysledky(data.prubehy || []); setVysledkyOtazky(data.otazky || []); }
            }).catch(() => {});
        }
    };
    const meta = [
        TYP_LABELS[skoleni.typ_zakonceni],
        skoleni.certifikace_typ_kod ? `certifikát ${skoleni.certifikace_typ_kod}${skoleni.certifikace_platnost_mesicu ? ` · platnost ${skoleni.certifikace_platnost_mesicu} měs.` : ''}` : null,
        skoleni.autor ? `autor ${skoleni.autor.jmeno} ${skoleni.autor.prijmeni}` : null,
    ].filter(Boolean).join(' · ');
    const aktivni = skoleni.stav === 'aktivni';

    return (
        <TmavyCtx.Provider value={isDark}>
        <MobilCtx.Provider value={isMobile}>
        <PotvrditCtx.Provider value={potvrdit}>
        <div className="flex flex-col flex-1 min-h-0">
            <div style={{ padding: isMobile ? '6px 12px 0' : '14px 18px 0', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`, flexShrink: 0 }}>
                {isMobile && (
                    <ZpetTlacitko onClick={onZavrit} popis="Zpět na seznam školení" sklo isDark={isDark} />
                )}
                <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
                    <h2 className="font-bold" style={{ fontSize: isMobile ? 18 : 19, color: 'var(--text-primary)' }}>{skoleni.nazev}</h2>
                    <span className="text-[11px] font-semibold rounded-full" style={{
                        padding: '2px 8px',
                        ...(aktivni
                            ? { color: '#34d399', background: 'rgba(5,150,105,0.12)', border: '1px solid rgba(5,150,105,0.35)' }
                            : { color: 'var(--text-secondary)', background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}` }),
                    }}>{STAV_LABEL[skoleni.stav] || skoleni.stav}</span>
                </div>
                {meta && <p className="text-[12.5px]" style={{ color: 'var(--text-secondary)', marginTop: 2 }}>{meta}</p>}
                {nadZalozkami && <div style={{ marginTop: 10 }}>{nadZalozkami}</div>}
                <div role="tablist" className="flex overflow-x-auto" style={{ gap: 2, marginTop: isMobile ? 6 : 10, scrollbarWidth: 'none' }}>
                    {zalozky.map(z => {
                        const vybrana = tab === z.id;
                        return (
                            <button key={z.id} type="button" role="tab" aria-selected={vybrana} onClick={() => prepni(z.id)}
                                className="whitespace-nowrap"
                                style={{
                                    height: isMobile ? 44 : 38, padding: '0 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                    background: 'none', border: 'none', marginBottom: -1,
                                    color: vybrana ? SECTION_COLOR : 'var(--text-secondary)',
                                    borderBottom: `2px solid ${vybrana ? SECTION_COLOR : 'transparent'}`,
                                }}>
                                {z.label}
                                {z.id === 'obsah' && skoleni.sekce?.length > 0 && <span style={{ color: 'var(--text-muted)', fontWeight: 500, marginLeft: 5 }}>{skoleni.sekce.length}</span>}
                                {z.id === 'kviz' && skoleni.otazky?.length > 0 && <span style={{ color: 'var(--text-muted)', fontWeight: 500, marginLeft: 5 }}>{skoleni.otazky.length}</span>}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="space-y-4" style={{
                flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain',
                padding: isMobile ? '12px 14px 18px' : '16px 18px 24px',
                ...(rezervaDole ? { paddingBottom: `calc(18px + ${rezervaDole})` } : null),
            }}>
                {tab === 'obsah' && <TabObsah skoleni={skoleni} onRefresh={obnovit} onEditace={onEditace} />}
                {tab === 'nahled' && <TabNahled skoleni={skoleni} />}
                {tab === 'kviz' && hasKviz && <TabKviz skoleni={skoleni} onRefresh={obnovit} />}
                {tab === 'nastaveni' && <TabNastaveni skoleni={skoleni} onSave={saveSkoleni} onRefresh={obnovit} onNavigate={onNavigate} />}
                {tab === 'prirazeni' && <TabPrirazeni skoleni={skoleni} ciselniky={ciselniky} onRefresh={obnovit} />}
                {tab === 'vysledky' && <TabVysledky vysledky={vysledky} otazky={vysledkyOtazky} />}
            </div>
            {potvrzeni}
        </div>
        </PotvrditCtx.Provider>
        </MobilCtx.Provider>
        </TmavyCtx.Provider>
    );
}

// ── Tab: Obsah ──────────────────────────────────────────────
function TabObsah({ skoleni, onRefresh, onEditace }) {
    const potvrdit = useContext(PotvrditCtx);
    const mobil = useContext(MobilCtx);
    const [editingSekce, setEditingSekce] = useState(null);
    useEffect(() => { onEditace?.(editingSekce != null); }, [editingSekce]); // eslint-disable-line react-hooks/exhaustive-deps
    const [addType, setAddType] = useState(null);
    const [addName, setAddName] = useState('');
    const [uploading, setUploading] = useState(null);
    const [dragIndex, setDragIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [localEdits, setLocalEdits] = useState({}); // { [sekceId]: { nazev, popis, obsah_html, video_url } }
    const debounceTimers = useRef({});

    const addSekce = async () => {
        if (!addName.trim() || !addType) return;
        await api(`/api/skoleni/${skoleni.id}/sekce`, { method: 'POST', body: JSON.stringify({ nazev: addName, typ: addType }) });
        setAddType(null);
        setAddName('');
        onRefresh();
    };

    const deleteSekce = async (sekceId) => {
        if (!(await potvrdit('Smazat sekci?', { text: 'Sekce zmizí i s obsahem. Nejde to vrátit.', potvrdit: 'Smazat' }))) return;
        await api(`/api/skoleni/${skoleni.id}/sekce/${sekceId}`, { method: 'DELETE' });
        onRefresh();
    };

    // Okamžité uložení + refresh (pro strukturální změny: název, pořadí)
    const updateSekce = async (sekceId, data) => {
        await api(`/api/skoleni/${skoleni.id}/sekce/${sekceId}`, { method: 'PUT', body: JSON.stringify(data) });
        onRefresh();
    };

    // Debounced uložení BEZ refreshe (pro textové pole, obsah TipTap, popis)
    const updateSekceSilent = (sekceId, data) => {
        // Okamžitě aktualizovat lokální stav (pro plynulé psaní)
        setLocalEdits(prev => ({ ...prev, [sekceId]: { ...(prev[sekceId] || {}), ...data } }));
        // Debounce API save
        const key = `${sekceId}-${Object.keys(data).join(',')}`;
        clearTimeout(debounceTimers.current[key]);
        debounceTimers.current[key] = setTimeout(() => {
            api(`/api/skoleni/${skoleni.id}/sekce/${sekceId}`, { method: 'PUT', body: JSON.stringify(data) });
        }, 800);
    };

    // Pomocná fce — vrátí hodnotu pole: lokální edit > server data
    const sekceVal = (s, field) => localEdits[s.id]?.[field] !== undefined ? localEdits[s.id][field] : (s[field] || '');

    // Při zavření editace uložit pending změny a refreshnout
    const closeEditing = (sekceId) => {
        // Flush pending debounce timers pro tuto sekci
        Object.keys(debounceTimers.current).forEach(key => {
            if (key.startsWith(`${sekceId}-`)) {
                clearTimeout(debounceTimers.current[key]);
                delete debounceTimers.current[key];
            }
        });
        // Uložit lokální změny okamžitě
        if (localEdits[sekceId]) {
            api(`/api/skoleni/${skoleni.id}/sekce/${sekceId}`, { method: 'PUT', body: JSON.stringify(localEdits[sekceId]) });
            setLocalEdits(prev => { const n = { ...prev }; delete n[sekceId]; return n; });
        }
        setEditingSekce(null);
        // Refresh po malé pauze aby API stihl uložit
        setTimeout(onRefresh, 300);
    };

    const uploadFile = async (sekceId, file) => {
        setUploading(sekceId);
        const fd = new FormData();
        fd.append('soubor', file);
        await fetch(`/api/skoleni/${skoleni.id}/sekce/${sekceId}/upload`, {
            method: 'POST', headers: { 'X-CSRF-TOKEN': token() }, body: fd,
        });
        setUploading(null);
        onRefresh();
    };

    // Drag & Drop handlers
    const handleDragStart = (e, index) => {
        setDragIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index);
    };
    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (index !== dragOverIndex) setDragOverIndex(index);
    };
    const handleDragEnd = () => { setDragIndex(null); setDragOverIndex(null); };
    const handleDrop = async (e, targetIndex) => {
        e.preventDefault();
        const sourceIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
        handleDragEnd();
        if (isNaN(sourceIndex) || sourceIndex === targetIndex) return;
        const reordered = [...skoleni.sekce];
        const [moved] = reordered.splice(sourceIndex, 1);
        reordered.splice(targetIndex, 0, moved);
        await api(`/api/skoleni/${skoleni.id}/sekce-poradi`, { method: 'PUT', body: JSON.stringify({ poradi: reordered.map(s => s.id) }) });
        onRefresh();
    };

    const fileInputRefs = {};

    return (
        <div className="space-y-3">
            {skoleni.sekce.length === 0 && (
                <div className="text-center py-8 text-sm" style={{ color: 'var(--text-muted)' }}>
                    Zatím žádné sekce. Přidejte první sekci níže.
                </div>
            )}

            {skoleni.sekce.map((s, i) => {
                const SIcon = SEKCE_TYP_ICONS[s.typ] || FileText;
                const isEditing = editingSekce === s.id;
                const isDragTarget = dragOverIndex === i && dragIndex !== i;

                return (
                    <div
                        key={s.id}
                        className="rounded-lg overflow-hidden"
                        draggable={!mobil}
                        onDragStart={e => handleDragStart(e, i)}
                        onDragOver={e => handleDragOver(e, i)}
                        onDragEnd={handleDragEnd}
                        onDrop={e => handleDrop(e, i)}
                        style={{
                            border: isDragTarget ? `2px solid ${SECTION_COLOR}` : '1px solid var(--border-default)',
                            opacity: dragIndex === i ? 0.5 : 1,
                            transition: 'border-color 0.15s, opacity 0.15s',
                        }}
                    >
                        {/* Section header */}
                        <div
                            className="flex items-center gap-2 px-4 py-2.5"
                            style={{ background: 'var(--surface-elevated)', cursor: 'pointer' }}
                            onClick={() => isEditing ? closeEditing(s.id) : setEditingSekce(s.id)}
                        >
                            {!mobil && <GripVertical size={14} style={{ color: 'var(--text-muted)', cursor: 'grab' }} />}
                            <SIcon size={14} style={{ color: SECTION_COLOR }} />
                            <span className="flex-1 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                                {i + 1}. {s.nazev}
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'var(--surface-base)', color: 'var(--text-muted)' }}>
                                {s.typ}
                            </span>
                            {!mobil && <button onClick={e => { e.stopPropagation(); deleteSekce(s.id); }} className="p-1 rounded" style={{ color: '#f87171', cursor: 'pointer', background: 'none', border: 'none' }}><Trash2 size={14} /></button>}
                        </div>

                        {/* Section content (expanded) */}
                        {isEditing && (
                            <div className="p-4 space-y-3" style={{ borderTop: '1px solid var(--border-default)' }}>
                                <div>
                                    <label className={labelCls} style={labelStyle}>Název sekce</label>
                                    <input
                                        value={sekceVal(s, 'nazev')}
                                        onChange={e => updateSekceSilent(s.id, { nazev: e.target.value })}
                                        className={inputCls}
                                        style={inputStyle}
                                    />
                                </div>

                                {s.typ === 'obsah' && (
                                    <div>
                                        <label className={labelCls} style={labelStyle}>Obsah</label>
                                        <TipTapEditor
                                            value={sekceVal(s, 'obsah_html')}
                                            onChange={html => updateSekceSilent(s.id, { obsah_html: html })}
                                            skoleniId={skoleni.id}
                                        />
                                    </div>
                                )}

                                {s.typ === 'video' && (
                                    <div className="space-y-3">
                                        <div>
                                            <label className={labelCls} style={labelStyle}>Popisek</label>
                                            <input
                                                value={sekceVal(s, 'popis')}
                                                onChange={e => updateSekceSilent(s.id, { popis: e.target.value })}
                                                placeholder="Krátký popis videa..."
                                                className={inputCls}
                                                style={inputStyle}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelCls} style={labelStyle}>Video URL (YouTube / Vimeo)</label>
                                            <input
                                                value={sekceVal(s, 'video_url')}
                                                onChange={e => updateSekceSilent(s.id, { video_url: e.target.value })}
                                                placeholder="https://www.youtube.com/watch?v=..."
                                                className={inputCls}
                                                style={inputStyle}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelCls} style={labelStyle}>Nebo nahrát video soubor</label>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    ref={el => fileInputRefs[`video-${s.id}`] = el}
                                                    type="file"
                                                    accept="video/*"
                                                    onChange={e => e.target.files[0] && uploadFile(s.id, e.target.files[0])}
                                                    style={{ display: 'none' }}
                                                />
                                                <button
                                                    onClick={() => fileInputRefs[`video-${s.id}`]?.click()}
                                                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium"
                                                    style={{ background: 'var(--surface-base)', border: '1px solid var(--border-default)', color: 'var(--text-secondary)', cursor: 'pointer' }}
                                                >
                                                    <Upload size={14} /> Vybrat soubor
                                                </button>
                                                {uploading === s.id && <span className="text-xs" style={{ color: 'var(--text-muted)' }}><Loader2 size={12} className="inline animate-spin" /> Nahrávám...</span>}
                                            </div>
                                            {s.video_cesta && <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Nahráno: {s.video_cesta.split('/').pop()}</p>}
                                        </div>
                                    </div>
                                )}

                                {s.typ === 'soubor' && (
                                    <div className="space-y-3">
                                        <div>
                                            <label className={labelCls} style={labelStyle}>Popisek</label>
                                            <input
                                                value={sekceVal(s, 'popis')}
                                                onChange={e => updateSekceSilent(s.id, { popis: e.target.value })}
                                                placeholder="Krátký popis souboru..."
                                                className={inputCls}
                                                style={inputStyle}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelCls} style={labelStyle}>Soubor (PDF)</label>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    ref={el => fileInputRefs[`soubor-${s.id}`] = el}
                                                    type="file"
                                                    accept=".pdf,application/pdf"
                                                    onChange={e => e.target.files[0] && uploadFile(s.id, e.target.files[0])}
                                                    style={{ display: 'none' }}
                                                />
                                                <button
                                                    onClick={() => fileInputRefs[`soubor-${s.id}`]?.click()}
                                                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium"
                                                    style={{ background: 'var(--surface-base)', border: '1px solid var(--border-default)', color: 'var(--text-secondary)', cursor: 'pointer' }}
                                                >
                                                    <Upload size={14} /> Vybrat soubor
                                                </button>
                                                {uploading === s.id && <span className="text-xs" style={{ color: 'var(--text-muted)' }}><Loader2 size={12} className="inline animate-spin" /> Nahrávám...</span>}
                                            </div>
                                            {s.soubor_nazev && (
                                                <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg text-sm" style={{ background: 'var(--surface-base)', border: '1px solid var(--border-default)', color: 'var(--text-secondary)' }}>
                                                    <FileDown size={14} /> {s.soubor_nazev}
                                                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>({Math.round((s.soubor_velikost || 0) / 1024)} KB)</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            {/* Add section */}
            {addType ? (
                <div className="p-4 rounded-lg" style={{ border: '1px dashed var(--border-strong)', background: 'var(--surface-elevated)' }}>
                    <div className="flex gap-2 items-end">
                        <div className="flex-1">
                            <label className={labelCls} style={labelStyle}>Název nové sekce ({addType})</label>
                            <input
                                value={addName}
                                onChange={e => setAddName(e.target.value)}
                                placeholder="Např. Úvod, Bezpečnost práce..."
                                className={inputCls}
                                style={inputStyle}
                                onKeyDown={e => e.key === 'Enter' && addSekce()}
                                autoFocus
                            />
                        </div>
                        <button onClick={addSekce} className="px-3 py-2 rounded-lg text-sm font-medium" style={{ background: SECTION_COLOR, color: '#fff', border: 'none', cursor: 'pointer' }}>Přidat</button>
                        <button onClick={() => { setAddType(null); setAddName(''); }} className="px-3 py-2 rounded-lg text-sm" style={{ color: 'var(--text-muted)', border: '1px solid var(--border-default)', background: 'transparent', cursor: 'pointer' }}>Zrušit</button>
                    </div>
                </div>
            ) : (
                <div className="flex gap-2">
                    {[
                        { typ: 'obsah', label: 'Text', Icon: FileText },
                        { typ: 'video', label: 'Video', Icon: Video },
                        { typ: 'soubor', label: 'Soubor', Icon: FileDown },
                    ].map(t => (
                        <button
                            key={t.typ}
                            onClick={() => setAddType(t.typ)}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium"
                            style={{ color: 'var(--text-secondary)', border: '1px dashed var(--border-strong)', background: 'transparent', cursor: 'pointer' }}
                        >
                            <Plus size={14} /> <t.Icon size={14} /> {t.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Tab: Náhled ─────────────────────────────────────────────
function TabNahled({ skoleni }) {
    const [current, setCurrent] = useState(0);
    const [showKviz, setShowKviz] = useState(false);
    const [pdfRead, setPdfRead] = useState({}); // { [sekceId]: true } — PDF přečteno do konce
    const sekce = skoleni.sekce || [];
    const otazky = skoleni.otazky || [];
    const hasKviz = skoleni.typ_zakonceni !== 'skoleni' && otazky.length > 0;

    if (sekce.length === 0) {
        return (
            <div className="text-center py-12 text-sm" style={{ color: 'var(--text-muted)' }}>
                Zatím žádné sekce. Přidejte obsah v záložce Obsah.
            </div>
        );
    }

    // Kvíz náhled
    if (showKviz) {
        return (
            <div className="space-y-4" style={{ maxWidth: 720, margin: '0 auto' }}>
                <div className="flex items-center gap-3">
                    <button onClick={() => setShowKviz(false)} className="p-2 rounded-lg" style={{ color: 'var(--text-muted)', border: '1px solid var(--border-default)', background: 'transparent', cursor: 'pointer' }}>
                        <ChevronLeft size={16} />
                    </button>
                    <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Kvíz — náhled</h2>
                </div>

                <div className="text-center py-4 rounded-lg" style={{ background: 'var(--surface-elevated)', border: '1px solid var(--border-default)' }}>
                    <ClipboardCheck size={24} style={{ color: SECTION_COLOR, margin: '0 auto 8px' }} />
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {otazky.length} {otazky.length === 1 ? 'otázka' : otazky.length < 5 ? 'otázky' : 'otázek'} &middot; Min. úspěšnost: {skoleni.min_uspesnost_pct || 80}%
                        {skoleni.max_pokusu ? ` · Max. ${skoleni.max_pokusu} pokusů` : ''}
                    </p>
                </div>

                {otazky.map((q, qi) => (
                    <div key={q.id} className="rounded-lg p-4" style={{ border: '1px solid var(--border-default)', background: 'var(--surface-elevated)' }}>
                        <p className="text-sm font-medium mb-3" style={{ color: 'var(--text-primary)' }}>
                            <span style={{ color: SECTION_COLOR, fontWeight: 700 }}>{qi + 1}.</span> {q.text_otazky}
                        </p>
                        <div className="space-y-1.5">
                            {q.odpovedi.map(o => (
                                <div
                                    key={o.id}
                                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm"
                                    style={{
                                        background: o.je_spravna ? 'rgba(16,185,129,0.08)' : 'var(--surface-base)',
                                        border: `1px solid ${o.je_spravna ? 'rgba(16,185,129,0.3)' : 'var(--border-default)'}`,
                                        color: o.je_spravna ? '#10b981' : 'var(--text-secondary)',
                                    }}
                                >
                                    <div style={{
                                        width: 18, height: 18, flexShrink: 0,
                                        borderRadius: q.typ === 'vyber_vice' ? 4 : '50%',
                                        border: `2px solid ${o.je_spravna ? '#10b981' : 'var(--border-strong)'}`,
                                        background: o.je_spravna ? '#10b981' : 'transparent',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        {o.je_spravna && <Check size={10} color="white" strokeWidth={3} />}
                                    </div>
                                    {o.text}
                                </div>
                            ))}
                        </div>
                        <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>Typ: {q.typ} · Body: {q.body}</p>
                    </div>
                ))}

                <button
                    onClick={() => setShowKviz(false)}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold mx-auto"
                    style={{ background: 'var(--surface-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border-default)', cursor: 'pointer' }}
                >
                    <ChevronLeft size={14} /> Zpět na obsah
                </button>
            </div>
        );
    }

    const aktivni = sekce[current];
    const SIcon = SEKCE_TYP_ICONS[aktivni.typ] || FileText;
    const isLast = current === sekce.length - 1;

    // Blokace "Další" u souboru — dokud PDF není přečteno do konce
    const nextDisabled = aktivni.typ === 'soubor' && aktivni.soubor_cesta && !pdfRead[aktivni.id];

    return (
        <div className="space-y-4" style={{ maxWidth: 720, margin: '0 auto' }}>
            {/* Stepper */}
            <div className="flex items-center justify-center gap-1.5 flex-wrap">
                {sekce.map((s, i) => (
                    <button
                        key={s.id}
                        onClick={() => setCurrent(i)}
                        style={{
                            width: 32, height: 32, borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 13, fontWeight: 600,
                            background: i === current ? SECTION_COLOR : 'var(--surface-elevated)',
                            color: i === current ? '#1a1a2e' : 'var(--text-muted)',
                            border: `2px solid ${i === current ? SECTION_COLOR : 'var(--border-default)'}`,
                            cursor: 'pointer', transition: 'all 0.15s',
                        }}
                        title={s.nazev}
                    >
                        {i + 1}
                    </button>
                ))}
                {hasKviz && (
                    <button
                        onClick={() => setShowKviz(true)}
                        style={{
                            width: 32, height: 32, borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'var(--surface-elevated)',
                            color: 'var(--text-muted)',
                            border: '2px solid var(--border-default)',
                            cursor: 'pointer', transition: 'all 0.15s',
                        }}
                        title="Kvíz"
                    >
                        <ClipboardCheck size={14} />
                    </button>
                )}
            </div>

            {/* Header info */}
            <div className="text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                Sekce {current + 1} / {sekce.length}
            </div>

            {/* Section card */}
            <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-default)' }}>
                <div className="px-4 py-3 flex items-center gap-2" style={{ background: 'var(--surface-elevated)', borderBottom: '1px solid var(--border-default)' }}>
                    <SIcon size={14} style={{ color: SECTION_COLOR }} />
                    <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{aktivni.nazev}</span>
                </div>

                <div className="p-5" style={{ background: 'var(--surface-base)' }}>
                    {aktivni.typ === 'obsah' && aktivni.obsah_html && (
                        <div
                            className="tiptap-content"
                            style={{ color: 'var(--text-primary)', fontSize: 14, lineHeight: 1.7 }}
                            dangerouslySetInnerHTML={{ __html: aktivni.obsah_html }}
                        />
                    )}
                    {aktivni.typ === 'obsah' && !aktivni.obsah_html && (
                        <p className="text-sm" style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Prázdný obsah</p>
                    )}

                    {aktivni.typ === 'video' && (
                        <div className="space-y-3">
                            {aktivni.popis && (
                                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{aktivni.popis}</p>
                            )}
                            {aktivni.video_url && (() => {
                                const url = aktivni.video_url;
                                let embedUrl = url;
                                const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
                                if (ytMatch) embedUrl = `https://www.youtube.com/embed/${ytMatch[1]}`;
                                const vmMatch = url.match(/vimeo\.com\/(\d+)/);
                                if (vmMatch) embedUrl = `https://player.vimeo.com/video/${vmMatch[1]}`;
                                return (
                                    <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: 6, overflow: 'hidden' }}>
                                        <iframe src={embedUrl} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }} allowFullScreen />
                                    </div>
                                );
                            })()}
                            {aktivni.video_cesta && !aktivni.video_url && (
                                <video controls style={{ width: '100%', borderRadius: 6 }} src={`/storage/${aktivni.video_cesta}`} />
                            )}
                            {!aktivni.video_url && !aktivni.video_cesta && (
                                <p className="text-sm" style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Žádné video</p>
                            )}
                        </div>
                    )}

                    {aktivni.typ === 'soubor' && (
                        <div className="space-y-3">
                            {aktivni.popis && (
                                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{aktivni.popis}</p>
                            )}
                            {aktivni.soubor_cesta ? (
                                <PdfViewer
                                    url={`/storage/${aktivni.soubor_cesta}`}
                                    nazev={aktivni.soubor_nazev}
                                    onLastPage={() => setPdfRead(prev => ({ ...prev, [aktivni.id]: true }))}
                                />
                            ) : (
                                <p className="text-sm" style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Žádný soubor</p>
                            )}
                            {nextDisabled && (
                                <p className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>
                                    Přečtěte dokument do konce pro pokračování
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between">
                <button
                    onClick={() => setCurrent(c => Math.max(0, c - 1))}
                    disabled={current <= 0}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium"
                    style={{
                        background: 'var(--surface-elevated)', border: '1px solid var(--border-default)',
                        color: current <= 0 ? 'var(--text-muted)' : 'var(--text-primary)',
                        cursor: current <= 0 ? 'not-allowed' : 'pointer', opacity: current <= 0 ? 0.5 : 1,
                    }}
                >
                    <ChevronLeft size={14} /> Předchozí
                </button>
                {isLast && hasKviz ? (
                    <button
                        onClick={() => setShowKviz(true)}
                        disabled={nextDisabled}
                        className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold"
                        style={{
                            background: nextDisabled ? 'var(--surface-elevated)' : SECTION_COLOR,
                            border: nextDisabled ? '1px solid var(--border-default)' : 'none',
                            color: nextDisabled ? 'var(--text-muted)' : '#1a1a2e',
                            cursor: nextDisabled ? 'not-allowed' : 'pointer',
                            opacity: nextDisabled ? 0.5 : 1,
                        }}
                    >
                        Pokračovat na kvíz <ChevronRight size={14} />
                    </button>
                ) : (
                    <button
                        onClick={() => setCurrent(c => Math.min(sekce.length - 1, c + 1))}
                        disabled={isLast || nextDisabled}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium"
                        style={{
                            background: (isLast || nextDisabled) ? 'var(--surface-elevated)' : SECTION_COLOR,
                            border: (isLast || nextDisabled) ? '1px solid var(--border-default)' : 'none',
                            color: (isLast || nextDisabled) ? 'var(--text-muted)' : '#1a1a2e',
                            cursor: (isLast || nextDisabled) ? 'not-allowed' : 'pointer',
                            opacity: (isLast || nextDisabled) ? 0.5 : 1,
                        }}
                    >
                        Další <ChevronRight size={14} />
                    </button>
                )}
            </div>
        </div>
    );
}

// ── Tab: Kvíz ───────────────────────────────────────────────
function TabKviz({ skoleni, onRefresh }) {
    const potvrdit = useContext(PotvrditCtx);
    const mobil = useContext(MobilCtx);
    const [editingQ, setEditingQ] = useState(null);
    const [newQ, setNewQ] = useState(null);
    const [reaktivace, setReaktivace] = useState(null); // null | 'loading' | { resetovano }

    const isAktivni = skoleni.stav === 'aktivni';

    const deleteOtazka = async (oId) => {
        if (!(await potvrdit('Smazat otázku?', { potvrdit: 'Smazat' }))) return;
        await api(`/api/skoleni/${skoleni.id}/otazky/${oId}`, { method: 'DELETE' });
        onRefresh();
    };

    const saveOtazka = async (data, oId) => {
        if (oId) {
            await api(`/api/skoleni/${skoleni.id}/otazky/${oId}`, { method: 'PUT', body: JSON.stringify(data) });
        } else {
            await api(`/api/skoleni/${skoleni.id}/otazky`, { method: 'POST', body: JSON.stringify(data) });
        }
        // Nechá formulář chvíli zobrazený pro "Uloženo" feedback, pak zavře
        setTimeout(() => {
            setEditingQ(null);
            setNewQ(null);
            onRefresh();
        }, 1200);
    };

    const handleReaktivace = async () => {
        if (!(await potvrdit('Reaktivovat školení?', { text: 'Nedokončené průběhy (přiřazeno, zahájeno) se resetují — tito lidé začnou školení znovu s aktuálním obsahem.\n\nDokončená školení zůstanou beze změny.', potvrdit: 'Reaktivovat', nebezpecne: false }))) return;
        setReaktivace('loading');
        const resp = await api(`/api/skoleni/${skoleni.id}/reaktivovat`, { method: 'POST' });
        setReaktivace({ resetovano: resp.resetovano_prubehu });
        onRefresh();
        setTimeout(() => setReaktivace(null), 4000);
    };

    return (
        <div className="space-y-3">
            {/* Info banner pro aktivní školení */}
            {isAktivni && (
                <div className="flex items-start gap-3 rounded-lg p-3" style={{
                    background: 'rgba(251,191,36,0.08)',
                    border: '1px solid rgba(251,191,36,0.25)',
                }}>
                    <AlertTriangle size={16} style={{ color: '#fbbf24', flexShrink: 0, marginTop: 1 }} />
                    <div className="flex-1">
                        <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                            Školení je aktivní
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                            Změny v kvízu se projeví pouze pro nové účastníky. Kdo už školení zahájil nebo dokončil, uvidí původní verzi.
                        </p>
                    </div>
                    <button
                        onClick={handleReaktivace}
                        disabled={reaktivace === 'loading'}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold shrink-0"
                        style={{
                            background: reaktivace?.resetovano !== undefined ? 'rgba(16,185,129,0.12)' : 'rgba(251,191,36,0.15)',
                            color: reaktivace?.resetovano !== undefined ? '#10b981' : '#fbbf24',
                            border: `1px solid ${reaktivace?.resetovano !== undefined ? 'rgba(16,185,129,0.3)' : 'rgba(251,191,36,0.3)'}`,
                            cursor: reaktivace === 'loading' ? 'wait' : 'pointer',
                        }}
                    >
                        {reaktivace === 'loading' && <><Loader2 size={12} className="animate-spin" /> Reaktivuji...</>}
                        {reaktivace?.resetovano !== undefined && <><Check size={12} /> Resetováno {reaktivace.resetovano} průběhů</>}
                        {!reaktivace && <><RefreshCw size={12} /> Reaktivovat</>}
                    </button>
                </div>
            )}

            {/* Feedback po reaktivaci */}

            {skoleni.otazky.length === 0 && !newQ && (
                <div className="text-center py-8 text-sm" style={{ color: 'var(--text-muted)' }}>Žádné kvízové otázky. Přidejte první otázku.</div>
            )}

            {skoleni.otazky.map((q, i) => (
                <div key={q.id} className="rounded-lg p-4" style={{ border: '1px solid var(--border-default)', background: 'var(--surface-elevated)' }}>
                    {editingQ === q.id ? (
                        <OtazkaForm
                            initial={q}
                            onSave={data => saveOtazka(data, q.id)}
                            onCancel={() => setEditingQ(null)}
                        />
                    ) : (
                        <div className="flex items-start gap-3">
                            <span className="text-sm font-bold shrink-0" style={{ color: SECTION_COLOR, minWidth: 24 }}>{i + 1}.</span>
                            <div className="flex-1">
                                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{q.text_otazky}</p>
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    {q.odpovedi.map(o => (
                                        <span key={o.id} className="text-xs px-2 py-0.5 rounded-full" style={{
                                            background: o.je_spravna ? 'rgba(16,185,129,0.12)' : 'var(--surface-base)',
                                            color: o.je_spravna ? '#10b981' : 'var(--text-muted)',
                                            border: `1px solid ${o.je_spravna ? 'rgba(16,185,129,0.25)' : 'var(--border-default)'}`,
                                        }}>
                                            {o.je_spravna && <Check size={10} className="inline mr-0.5" />}{o.text}
                                        </span>
                                    ))}
                                </div>
                                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Typ: {q.typ} &middot; Body: {q.body}</p>
                            </div>
                            <div className="flex gap-1">
                                <button onClick={() => setEditingQ(q.id)} className="p-1.5 rounded-lg text-xs" style={{ color: 'var(--text-muted)', cursor: 'pointer' }}>Upravit</button>
                                {!mobil && <button onClick={() => deleteOtazka(q.id)} className="p-1.5 rounded-lg" style={{ color: '#f87171', cursor: 'pointer' }}><Trash2 size={14} /></button>}
                            </div>
                        </div>
                    )}
                </div>
            ))}

            {newQ ? (
                <div className="rounded-lg p-4" style={{ border: '1px dashed var(--border-strong)', background: 'var(--surface-elevated)' }}>
                    <OtazkaForm
                        onSave={data => saveOtazka(data, null)}
                        onCancel={() => setNewQ(null)}
                    />
                </div>
            ) : (
                <button
                    onClick={() => setNewQ(true)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
                    style={{ color: 'var(--text-secondary)', border: '1px dashed var(--border-strong)', background: 'transparent', cursor: 'pointer' }}
                >
                    <Plus size={14} /> Přidat otázku
                </button>
            )}
        </div>
    );
}

function OtazkaForm({ initial, onSave, onCancel }) {
    const [form, setForm] = useState({
        text_otazky: initial?.text_otazky || '',
        typ: initial?.typ || 'vyber_jedne',
        body: initial?.body || 1,
        odpovedi: initial?.odpovedi?.map(o => ({ id: o.id, text: o.text, je_spravna: o.je_spravna })) || [
            { text: '', je_spravna: true },
            { text: '', je_spravna: false },
        ],
    });
    const [saveState, setSaveState] = useState(null); // null | 'saving' | 'saved'

    const handleSave = async () => {
        setSaveState('saving');
        await onSave(form);
        setSaveState('saved');
        setTimeout(() => setSaveState(null), 1500);
    };

    const addOdpoved = () => setForm(f => ({ ...f, odpovedi: [...f.odpovedi, { text: '', je_spravna: false }] }));
    const removeOdpoved = (i) => setForm(f => ({ ...f, odpovedi: f.odpovedi.filter((_, idx) => idx !== i) }));
    const updateOdpoved = (i, key, val) => setForm(f => {
        const copy = [...f.odpovedi];
        copy[i] = { ...copy[i], [key]: val };
        // For vyber_jedne / ano_ne: only one can be correct
        if (key === 'je_spravna' && val && f.typ !== 'vyber_vice') {
            copy.forEach((o, idx) => { if (idx !== i) copy[idx] = { ...o, je_spravna: false }; });
        }
        return { ...f, odpovedi: copy };
    });

    return (
        <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="md:col-span-3">
                    <label className={labelCls} style={labelStyle}>Text otázky</label>
                    <input value={form.text_otazky} onChange={e => setForm(f => ({ ...f, text_otazky: e.target.value }))} className={inputCls} style={inputStyle} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <label className={labelCls} style={labelStyle}>Typ</label>
                        <Vyber value={form.typ} onChange={v => setForm(f => ({ ...f, typ: v }))}
                            options={[{ value: 'vyber_jedne', label: 'Výběr jedné' }, { value: 'vyber_vice', label: 'Výběr více' }, { value: 'ano_ne', label: 'Ano / Ne' }]} />
                    </div>
                    <div>
                        <label className={labelCls} style={labelStyle}>Body</label>
                        <input type="number" min={1} value={form.body} onChange={e => setForm(f => ({ ...f, body: parseInt(e.target.value) || 1 }))} className={inputCls} style={inputStyle} />
                    </div>
                </div>
            </div>

            <div>
                <label className={labelCls} style={labelStyle}>Odpovědi</label>
                <div className="space-y-1.5">
                    {form.odpovedi.map((o, i) => (
                        <div key={i} className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => updateOdpoved(i, 'je_spravna', !o.je_spravna)}
                                className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center"
                                style={{
                                    background: o.je_spravna ? '#10b981' : 'transparent',
                                    border: `2px solid ${o.je_spravna ? '#10b981' : 'var(--border-strong)'}`,
                                    cursor: 'pointer',
                                }}
                            >
                                {o.je_spravna && <Check size={12} color="white" strokeWidth={3} />}
                            </button>
                            <input
                                value={o.text}
                                onChange={e => updateOdpoved(i, 'text', e.target.value)}
                                placeholder={`Odpověď ${i + 1}`}
                                className="flex-1 px-3 py-1.5 rounded-lg text-sm"
                                style={inputStyle}
                            />
                            {form.odpovedi.length > 2 && (
                                <button onClick={() => removeOdpoved(i)} className="p-1" style={{ color: '#f87171', cursor: 'pointer' }}><X size={14} /></button>
                            )}
                        </div>
                    ))}
                </div>
                <button onClick={addOdpoved} className="mt-2 text-xs px-2 py-1 rounded" style={{ color: 'var(--text-muted)', cursor: 'pointer' }}>+ Další odpověď</button>
            </div>

            <div className="flex gap-2">
                <button
                    onClick={handleSave}
                    disabled={saveState === 'saving' || !form.text_otazky.trim() || form.odpovedi.some(o => !o.text.trim())}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold"
                    style={{
                        background: saveState === 'saved' ? '#10b981' : SECTION_COLOR,
                        color: saveState === 'saved' ? 'white' : '#1a1a2e',
                        border: 'none', cursor: saveState === 'saving' ? 'wait' : 'pointer',
                        transition: 'background 0.3s',
                    }}
                >
                    {saveState === 'saving' && <><Loader2 size={14} className="animate-spin" /> Ukládám...</>}
                    {saveState === 'saved' && <><Check size={14} /> Uloženo</>}
                    {!saveState && 'Uložit'}
                </button>
                <button onClick={onCancel} disabled={saveState === 'saving'} className="px-4 py-2 rounded-lg text-sm" style={{ color: 'var(--text-muted)', border: '1px solid var(--border-default)', background: 'transparent', cursor: 'pointer' }}>Zrušit</button>
            </div>
        </div>
    );
}

// ── Tab: Nastavení ──────────────────────────────────────────
function TabNastaveni({ skoleni, onSave, onRefresh, onNavigate }) {
    const potvrdit = useContext(PotvrditCtx);
    const mobil = useContext(MobilCtx);
    const [form, setForm] = useState({
        nazev: skoleni.nazev,
        popis: skoleni.popis || '',
        typ_zakonceni: skoleni.typ_zakonceni,
        min_uspesnost_pct: skoleni.min_uspesnost_pct || 80,
        max_pokusu: skoleni.max_pokusu || '',
        certifikace_platnost_mesicu: skoleni.certifikace_platnost_mesicu || '',
        certifikace_typ_kod: skoleni.certifikace_typ_kod || '',
        kvalifikace_dovednost_id: skoleni.kvalifikace_dovednost_id || '',
        kvalifikace_uroven: skoleni.kvalifikace_uroven ?? '',
    });

    const [dovednosti, setDovednosti] = useState([]);
    const [certTypy, setCertTypy] = useState([]);
    const [saveState, setSaveState] = useState(null); // null | 'saving' | 'saved' | 'error'
    useEffect(() => {
        fetch('/api/ciselniky/dovednosti').then(r => r.json()).then(setDovednosti);
        fetch('/api/certifikace').then(r => r.json()).then(setCertTypy);
    }, []);

    const hasKviz = form.typ_zakonceni !== 'skoleni';

    const handleSave = async () => {
        const data = { ...form };
        if (!data.max_pokusu) data.max_pokusu = null;
        if (!data.certifikace_platnost_mesicu) data.certifikace_platnost_mesicu = null;
        if (!data.certifikace_typ_kod) data.certifikace_typ_kod = null;
        if (!data.kvalifikace_dovednost_id) data.kvalifikace_dovednost_id = null;
        if (data.kvalifikace_uroven === '') data.kvalifikace_uroven = null;
        setSaveState('saving');
        try {
            await onSave(data);
            setSaveState('saved');
            setTimeout(() => setSaveState(null), 2000);
        } catch {
            setSaveState('error');
            setTimeout(() => setSaveState(null), 3000);
        }
    };

    const changeStav = async (novy) => {
        await api(`/api/skoleni/${skoleni.id}/stav`, { method: 'PUT', body: JSON.stringify({ stav: novy }) });
        onRefresh();
    };

    const deleteSkoleni = async () => {
        if (!(await potvrdit('Smazat školení?', { text: 'Školení zmizí i s průběhy a výsledky. Nejde to vrátit.', potvrdit: 'Smazat' }))) return;
        await api(`/api/skoleni/${skoleni.id}`, { method: 'DELETE' });
        onNavigate('zamestnanci', { tab: 'skoleni' });
    };

    return (
        <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                    <label className={labelCls} style={labelStyle}>Název</label>
                    <input value={form.nazev} onChange={e => setForm(f => ({ ...f, nazev: e.target.value }))} className={inputCls} style={inputStyle} />
                </div>
                <div className="md:col-span-2">
                    <label className={labelCls} style={labelStyle}>Popis</label>
                    <textarea value={form.popis} onChange={e => setForm(f => ({ ...f, popis: e.target.value }))} rows={3} className={inputCls} style={inputStyle} />
                </div>
                <div>
                    <label className={labelCls} style={labelStyle}>Typ zakončení</label>
                    <Vyber value={form.typ_zakonceni} onChange={v => setForm(f => ({ ...f, typ_zakonceni: v }))}
                        options={Object.entries(TYP_LABELS).map(([k, v]) => ({ value: k, label: v }))} />
                </div>
            </div>

            {hasKviz && (
                <div className="rounded-lg p-4" style={{ border: '1px solid var(--border-default)', background: 'var(--surface-elevated)' }}>
                    <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Nastavení kvízu</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls} style={labelStyle}>Minimální úspěšnost (%)</label>
                            <input type="number" min={1} max={100} value={form.min_uspesnost_pct} onChange={e => setForm(f => ({ ...f, min_uspesnost_pct: parseInt(e.target.value) || 80 }))} className={inputCls} style={inputStyle} />
                        </div>
                        <div>
                            <label className={labelCls} style={labelStyle}>Max. pokusů (prázdné = neomezeno)</label>
                            <input type="number" min={1} value={form.max_pokusu} onChange={e => setForm(f => ({ ...f, max_pokusu: e.target.value }))} className={inputCls} style={inputStyle} />
                        </div>
                    </div>
                </div>
            )}

            {form.typ_zakonceni === 'kviz_certifikace' && (
                <div className="rounded-lg p-4" style={{ border: '1px solid var(--border-default)', background: 'var(--surface-elevated)' }}>
                    <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Certifikace</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls} style={labelStyle}>Certifikát k obnovení</label>
                            <Vyber value={form.certifikace_typ_kod} onChange={v => setForm(f => ({ ...f, certifikace_typ_kod: v }))}
                                options={[{ value: '', label: 'Vyberte…' }, ...certTypy.map(c => ({ value: c.kod, label: `${c.odznak || ''} ${c.nazev}`.trim() }))]} />
                        </div>
                        <div>
                            <label className={labelCls} style={labelStyle}>Platnost certifikace (měsíce)</label>
                            <input type="number" min={1} value={form.certifikace_platnost_mesicu} onChange={e => setForm(f => ({ ...f, certifikace_platnost_mesicu: e.target.value }))} className={inputCls} style={inputStyle} />
                        </div>
                    </div>
                </div>
            )}

            {form.typ_zakonceni === 'kviz_kvalifikace' && (
                <div className="rounded-lg p-4" style={{ border: '1px solid var(--border-default)', background: 'var(--surface-elevated)' }}>
                    <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Kvalifikační matice</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls} style={labelStyle}>Dovednost k posunu</label>
                            <Vyber value={form.kvalifikace_dovednost_id} onChange={v => setForm(f => ({ ...f, kvalifikace_dovednost_id: v }))}
                                options={[{ value: '', label: 'Vyberte…' }, ...dovednosti.map(d => ({ value: d.id, label: `${d.kod} – ${d.nazev}` }))]} />
                        </div>
                        <div>
                            <label className={labelCls} style={labelStyle}>Cílová úroveň po úspěchu</label>
                            <Vyber value={form.kvalifikace_uroven} onChange={v => setForm(f => ({ ...f, kvalifikace_uroven: v }))}
                                options={[{ value: '', label: 'Vyberte…' }, { value: 1, label: '1 – Základy' }, { value: 2, label: '2 – Pokročilý' }, { value: 3, label: '3 – Expert' }]} />
                        </div>
                    </div>
                </div>
            )}

            <div className="flex items-center gap-3 flex-wrap">
                <button
                    onClick={handleSave}
                    disabled={saveState === 'saving'}
                    className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold"
                    style={{
                        background: saveState === 'saved' ? '#10b981' : saveState === 'error' ? '#f87171' : SECTION_COLOR,
                        color: (saveState === 'saved' || saveState === 'error') ? 'white' : '#1a1a2e',
                        border: 'none',
                        cursor: saveState === 'saving' ? 'wait' : 'pointer',
                        transition: 'background 0.3s',
                    }}
                >
                    {saveState === 'saving' && <><Loader2 size={14} className="animate-spin" /> Ukládám...</>}
                    {saveState === 'saved' && <><Check size={14} /> Uloženo</>}
                    {saveState === 'error' && <><X size={14} /> Chyba při ukládání</>}
                    {!saveState && <><Save size={14} /> Uložit nastavení</>}
                </button>

                {skoleni.stav === 'koncept' && (
                    <button onClick={() => changeStav('aktivni')} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium" style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981', border: '1px solid rgba(16,185,129,0.25)', cursor: 'pointer' }}>
                        <Play size={14} /> Aktivovat
                    </button>
                )}
                {skoleni.stav === 'aktivni' && (
                    <button onClick={() => changeStav('archivovano')} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium" style={{ color: 'var(--text-muted)', border: '1px solid var(--border-default)', background: 'transparent', cursor: 'pointer' }}>
                        <Archive size={14} /> Archivovat
                    </button>
                )}
                {skoleni.stav === 'archivovano' && (
                    <button onClick={() => changeStav('aktivni')} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium" style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981', border: '1px solid rgba(16,185,129,0.25)', cursor: 'pointer' }}>
                        <Play size={14} /> Znovu aktivovat
                    </button>
                )}
                {skoleni.stav === 'koncept' && !mobil && (
                    <button onClick={deleteSkoleni} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium" style={{ color: '#f87171', border: '1px solid rgba(248,113,113,0.25)', background: 'transparent', cursor: 'pointer' }}>
                        <Trash2 size={14} /> Smazat
                    </button>
                )}
            </div>
        </div>
    );
}

// ── Tab: Přiřazení ──────────────────────────────────────────
function TabPrirazeni({ skoleni, ciselniky, onRefresh }) {
    const potvrdit = useContext(PotvrditCtx);
    const mobil = useContext(MobilCtx);
    const [form, setForm] = useState({ typ_cile: 'zamestnanec', cil_id: '', termin_do: '', preskocit_hotove: true, hotove_pred: '' });
    const [saving, setSaving] = useState(false);

    const cilOptions = () => {
        switch (form.typ_cile) {
            case 'zamestnanec': return ciselniky.zamestnanci.map(z => ({ id: z.id, label: `${z.prijmeni} ${z.jmeno}` }));
            case 'oddeleni':   return ciselniky.oddeleni.map(o => ({ id: o.id, label: o.nazev }));
            case 'pozice':     return ciselniky.pozice.map(p => ({ id: p.id, label: p.nazev }));
            case 'kariera':    return ciselniky.kariery.map(k => ({ id: k.id, label: k.nazev }));
            default: return [];
        }
    };

    const handleAdd = async () => {
        if ((form.typ_cile !== 'certifikat' && !form.cil_id) || saving) return;
        setSaving(true);
        await api(`/api/skoleni/${skoleni.id}/prirazeni`, {
            method: 'POST',
            body: JSON.stringify({
                typ_cile: form.typ_cile,
                cil_id: form.cil_id ? parseInt(form.cil_id) : null,
                termin_do: form.termin_do || null,
                preskocit_hotove: form.preskocit_hotove,
                hotove_pred: form.hotove_pred || null,
            }),
        });
        setForm({ typ_cile: 'zamestnanec', cil_id: '', termin_do: '', preskocit_hotove: true, hotove_pred: '' });
        setSaving(false);
        onRefresh();
    };

    const handleDelete = async (pId) => {
        if (!(await potvrdit('Zrušit přiřazení?', { potvrdit: 'Zrušit přiřazení' }))) return;
        await api(`/api/skoleni/${skoleni.id}/prirazeni/${pId}`, { method: 'DELETE' });
        onRefresh();
    };

    const hasCert = !!skoleni.certifikace_typ_kod;
    const typLabels = {
        zamestnanec: 'Zaměstnanec', oddeleni: 'Oddělení', pozice: 'Pozice', kariera: 'Kariéra',
        ...(hasCert ? { certifikat: `Všichni s požadavkem na ${skoleni.certifikace_typ_kod}` } : {}),
    };

    return (
        <div className="space-y-4">
            {/* Add assignment */}
            <div className="rounded-lg p-4" style={{ border: '1px solid var(--border-default)', background: 'var(--surface-elevated)' }}>
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Nové přiřazení</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                        <label className={labelCls} style={labelStyle}>Typ cíle</label>
                        <Vyber value={form.typ_cile} onChange={v => setForm(f => ({ ...f, typ_cile: v, cil_id: '' }))}
                            options={Object.entries(typLabels).map(([k, v]) => ({ value: k, label: v }))} />
                    </div>
                    {form.typ_cile !== 'certifikat' && (
                        <div>
                            <label className={labelCls} style={labelStyle}>{typLabels[form.typ_cile]}</label>
                            <Vyber value={form.cil_id} onChange={v => setForm(f => ({ ...f, cil_id: v }))}
                                options={[{ value: '', label: 'Vyberte…' }, ...cilOptions().map(o => ({ value: o.id, label: o.label }))]} />
                        </div>
                    )}
                    {form.typ_cile === 'certifikat' && (
                        <div className="flex items-center text-sm" style={{ color: 'var(--text-secondary)' }}>
                            Přiřadí všem aktivním zaměstnancům, jejichž kariéra vyžaduje certifikát <strong style={{ color: 'var(--text-primary)', marginLeft: 4 }}>{skoleni.certifikace_typ_kod}</strong>
                        </div>
                    )}
                    <div>
                        <label className={labelCls} style={labelStyle}>Termín do</label>
                        <input type="date" value={form.termin_do} onChange={e => setForm(f => ({ ...f, termin_do: e.target.value }))} className={inputCls} style={inputStyle} />
                    </div>
                    <div className="flex items-end">
                        <button onClick={handleAdd} disabled={(form.typ_cile !== 'certifikat' && !form.cil_id) || saving} className="w-full px-4 py-2 rounded-lg text-sm font-semibold" style={{ background: SECTION_COLOR, color: '#fff', border: 'none', cursor: 'pointer', opacity: (form.typ_cile !== 'certifikat' && !form.cil_id) ? 0.5 : 1 }}>
                            {saving ? 'Přiřazuji...' : 'Přiřadit'}
                        </button>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-4 mt-3 pt-3" style={{ borderTop: '1px solid var(--border-default)' }}>
                    <label className="flex items-center gap-2 cursor-pointer text-sm" style={{ color: 'var(--text-primary)' }}>
                        <input type="checkbox" checked={form.preskocit_hotove}
                            onChange={e => setForm(f => ({ ...f, preskocit_hotove: e.target.checked }))}
                            style={{ accentColor: SECTION_COLOR }} />
                        Přeskočit zaměstnance, kteří již absolvovali
                    </label>
                    {form.preskocit_hotove && (
                        <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                            absolvované od:
                            <input type="date" value={form.hotove_pred}
                                onChange={e => setForm(f => ({ ...f, hotove_pred: e.target.value }))}
                                className={inputCls} style={{ ...inputStyle, width: 160 }} />
                        </label>
                    )}
                </div>
            </div>

            {/* Existing assignments */}
            {skoleni.prirazeni.length === 0 ? (
                <div className="text-center py-6 text-sm" style={{ color: 'var(--text-muted)' }}>Zatím žádná přiřazení.</div>
            ) : (
                <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-default)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: 'var(--surface-elevated)' }}>
                                {['Typ', 'Cíl', 'Termín', 'Přiřadil', 'Datum', ''].map(h => (
                                    <th key={h} className="text-left px-4 py-2 text-xs font-semibold" style={{ color: 'var(--text-muted)', textTransform: 'uppercase' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {skoleni.prirazeni.map(p => (
                                <tr key={p.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                                    <td className="px-4 py-2.5 text-sm" style={{ color: 'var(--text-secondary)' }}>{typLabels[p.typ_cile]}</td>
                                    <td className="px-4 py-2.5 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{p.cil_id}</td>
                                    <td className="px-4 py-2.5 text-sm" style={{ color: 'var(--text-secondary)' }}>{formatDate(p.termin_do)}</td>
                                    <td className="px-4 py-2.5 text-xs" style={{ color: 'var(--text-muted)' }}>{p.prirazil ? `${p.prirazil.jmeno} ${p.prirazil.prijmeni}` : '—'}</td>
                                    <td className="px-4 py-2.5 text-xs" style={{ color: 'var(--text-muted)' }}>{formatDate(p.created_at)}</td>
                                    <td className="px-4 py-2.5">{!mobil && <button onClick={() => handleDelete(p.id)} className="p-1" style={{ color: '#f87171', cursor: 'pointer' }}><Trash2 size={14} /></button>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ── Tab: Výsledky ───────────────────────────────────────────
function TabVysledky({ vysledky, otazky }) {
    const [kvizTooltip, setKvizTooltip] = useState(null); // { anchorRect, kvizVysledky }

    const stavBadge = {
        prirazeno: { label: 'Přiřazeno', color: '#6b7280' },
        zahajeno:  { label: 'Zahájeno',  color: '#fbbf24' },
        dokonceno: { label: 'Dokončeno', color: '#10b981' },
        neuspesne: { label: 'Neúspěšné', color: '#f87171' },
    };

    const formatTime = (s) => {
        if (!s) return '—';
        const m = Math.floor(s / 60);
        const sec = s % 60;
        return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
    };

    if (vysledky.length === 0) {
        return <div className="text-center py-8 text-sm" style={{ color: 'var(--text-muted)' }}>Zatím žádné výsledky.</div>;
    }

    const sortedOtazky = [...(otazky || [])].sort((a, b) => (a.poradi || 0) - (b.poradi || 0));
    const hasKviz = sortedOtazky.length > 0;

    return (
        <>
        <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-default)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ background: 'var(--surface-elevated)' }}>
                        {['Zaměstnanec', 'Stav', 'Zahájeno', 'Dokončeno', 'Čas', 'Kvíz', 'Pokus'].map(h => (
                            <th key={h} className="text-left px-4 py-2 text-xs font-semibold" style={{ color: 'var(--text-muted)', textTransform: 'uppercase' }}>{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {vysledky.map(p => {
                        const badge = stavBadge[p.stav] || stavBadge.prirazeno;
                        const lastKviz = p.kviz_vysledky?.[p.kviz_vysledky.length - 1];
                        const hasResults = p.kviz_vysledky?.length > 0 && hasKviz;
                        return (
                            <tr key={p.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                                <td className="px-4 py-2.5 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                                    {p.zamestnanec ? `${p.zamestnanec.prijmeni} ${p.zamestnanec.jmeno}` : '—'}
                                </td>
                                <td className="px-4 py-2.5">
                                    <span className="text-xs font-medium" style={{ color: badge.color }}>{badge.label}</span>
                                </td>
                                <td className="px-4 py-2.5 text-xs" style={{ color: 'var(--text-muted)' }}>{formatDateTime(p.zahajeno_at)}</td>
                                <td className="px-4 py-2.5 text-xs" style={{ color: 'var(--text-muted)' }}>{formatDateTime(p.dokonceno_at)}</td>
                                <td className="px-4 py-2.5 text-sm" style={{ color: 'var(--text-secondary)' }}>{formatTime(p.cas_celkem_s)}</td>
                                <td className="px-4 py-2.5 text-sm"
                                    style={{ color: lastKviz?.uspel ? '#10b981' : lastKviz ? '#f87171' : 'var(--text-muted)', cursor: hasResults ? 'pointer' : 'default' }}
                                    onMouseEnter={e => hasResults && setKvizTooltip({ anchorRect: e.currentTarget.getBoundingClientRect(), kvizVysledky: p.kviz_vysledky })}
                                    onMouseLeave={() => setKvizTooltip(null)}
                                >
                                    {lastKviz ? `${lastKviz.uspesnost_pct}%` : '—'}
                                </td>
                                <td className="px-4 py-2.5 text-xs" style={{ color: 'var(--text-muted)' }}>{p.pokus_cislo}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
        {kvizTooltip && <KvizPortalTooltip anchorRect={kvizTooltip.anchorRect} kvizVysledky={kvizTooltip.kvizVysledky} sortedOtazky={sortedOtazky} />}
        </>
    );
}

function KvizPortalTooltip({ anchorRect, kvizVysledky, sortedOtazky }) {
    const ref = useRef(null);
    const [pos, setPos] = useState({ top: 0, left: 0 });

    useLayoutEffect(() => {
        if (!ref.current || !anchorRect) return;
        const tt = ref.current.getBoundingClientRect();
        let left = anchorRect.right - tt.width;
        if (left < 8) left = 8;
        if (left + tt.width > window.innerWidth - 8) left = window.innerWidth - 8 - tt.width;
        let top = anchorRect.bottom + 6;
        if (top + tt.height > window.innerHeight - 8) {
            top = anchorRect.top - tt.height - 6;
        }
        setPos({ top, left });
    }, [anchorRect]);

    return ReactDOM.createPortal(
        <div ref={ref} style={{
            position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999,
            minWidth: 280, padding: '10px 12px', borderRadius: 9,
            background: 'var(--surface-elevated)', border: '1px solid var(--border-default)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
            pointerEvents: 'none', whiteSpace: 'nowrap',
        }}>
            <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Odpovědi na kvíz</div>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                    <tr>
                        <th className="text-left px-2 py-1 text-xs" style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Pokus</th>
                        {sortedOtazky.map((o, i) => (
                            <th key={o.id} className="text-center px-1.5 py-1 text-xs" style={{ color: 'var(--text-muted)', fontWeight: 600 }} title={o.text_otazky}>Q{i + 1}</th>
                        ))}
                        <th className="text-center px-2 py-1 text-xs" style={{ color: 'var(--text-muted)', fontWeight: 600 }}>%</th>
                    </tr>
                </thead>
                <tbody>
                    {kvizVysledky.map((kv, ki) => (
                        <tr key={kv.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                            <td className="px-2 py-1 text-xs" style={{ color: 'var(--text-secondary)' }}>#{ki + 1}</td>
                            {sortedOtazky.map(o => {
                                const odp = kv.odpovedi_zam?.find(a => a.otazka_id === o.id);
                                return (
                                    <td key={o.id} className="text-center px-1.5 py-1">
                                        {odp ? (
                                            odp.je_spravna
                                                ? <Check size={14} style={{ color: '#10b981', display: 'inline' }} />
                                                : <X size={14} style={{ color: '#f87171', display: 'inline' }} />
                                        ) : (
                                            <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>—</span>
                                        )}
                                    </td>
                                );
                            })}
                            <td className="text-center px-2 py-1 text-xs font-semibold" style={{ color: kv.uspel ? '#10b981' : '#f87171' }}>
                                {kv.uspesnost_pct}%
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>,
        document.body
    );
}
